import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, IconButton } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import {
    SCORING_PRIORITIES,
    type LeadPriorityScore,
} from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { useElementSize } from "app/pages/sales-intelligence/hooks/useElementSize";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";

import { StyledAddRuleOption, StyledAddRuleOptionLabel } from "./styles";

type Props = {
    onSelectPriority: (priority: LeadPriorityScore) => void;
};

const ADD_RULE_DROPDOWN_MIN_WIDTH_PX = 260;

export const LeadScoreAddRuleCombobox = ({ onSelectPriority }: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const { ref: triggerRef, size: triggerSize } = useElementSize();

    const handleSelect = (priority: LeadPriorityScore) => {
        setIsOpen(false);
        onSelectPriority(priority);
    };

    const dropdownWidthPx = Math.max(triggerSize?.width ?? 0, ADD_RULE_DROPDOWN_MIN_WIDTH_PX);

    return (
        <Stack
            gap="4px"
            alignItems="flex-start"
        >
            <Combobox
                placement="top-start"
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                width={`${dropdownWidthPx}px`}
                data-automation="lead-score-filter-add-rule-combobox"
            >
                <Combobox.Trigger ref={triggerRef}>
                    <IconButton
                        type="flat"
                        dataAutomation="lead-score-filter-add-rules"
                        iconName="add"
                    >
                        {translate(leadsSearchFiltersI18nKeys.leadScoresEmptyCta())}
                    </IconButton>
                </Combobox.Trigger>
                <Combobox.Content
                    selectionMode="single"
                    ariaLabel={translate(leadsSearchFiltersI18nKeys.leadScoresEmptyCta())}
                >
                    {SCORING_PRIORITIES.map((priority) => (
                        <StyledAddRuleOption
                            key={priority}
                            id={priority}
                            textValue={translate(leadsSearchFiltersI18nKeys.leadScoresAddRule(priority))}
                            onClick={() => handleSelect(priority)}
                            data-automation={`lead-score-filter-add-rule-${priority}`}
                        >
                            <Combobox.OptionLabel>
                                <StyledAddRuleOptionLabel>
                                    {translate(leadsSearchFiltersI18nKeys.leadScoresAddRule(priority))}
                                </StyledAddRuleOptionLabel>
                            </Combobox.OptionLabel>
                        </StyledAddRuleOption>
                    ))}
                </Combobox.Content>
            </Combobox>
        </Stack>
    );
};
